import React from 'react';
import { Link } from 'react-router-dom';
import { FiChevronRight, FiShield } from 'react-icons/fi';
import { useSettings } from '../context/SettingsContext';

const Section = ({ title, children }) => (
  <div className="mb-8">
    <h2 className="text-lg font-bold text-gray-800 mb-3 flex items-center gap-2">
      <span className="w-1 h-5 bg-primary-600 rounded-full inline-block" />
      {title}
    </h2>
    <div className="text-gray-600 text-sm leading-relaxed space-y-2">{children}</div>
  </div>
);

const PrivacyPolicyPage = () => {
  const { settings } = useSettings();
  const siteName = settings.siteName || 'Hospitex';

  return (
    <div className="bg-gray-50 min-h-screen">
      <div className="bg-primary-800 text-white py-10">
        <div className="container-custom">
          <nav className="flex items-center gap-1.5 text-sm text-primary-300 mb-4">
            <Link to="/" className="hover:text-white transition-colors">Home</Link>
            <FiChevronRight size={12} />
            <span className="text-white">Privacy Policy</span>
          </nav>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-white/10 rounded-xl flex items-center justify-center">
              <FiShield size={22} />
            </div>
            <div>
              <h1 className="text-2xl md:text-3xl font-extrabold">Privacy Policy</h1>
              <p className="text-primary-200 text-sm mt-0.5">Last updated: {new Date().toLocaleDateString('en-PK', { year: 'numeric', month: 'long', day: 'numeric' })}</p>
            </div>
          </div>
        </div>
      </div>

      <div className="container-custom py-10">
        <div className="max-w-3xl mx-auto bg-white rounded-2xl border border-gray-100 shadow-sm p-8">
          <p className="text-gray-600 text-sm leading-relaxed mb-8">
            {siteName} ("we", "us", or "our") is committed to protecting your personal information. This Privacy Policy explains how we collect, use, and safeguard your data when you visit our website or make a purchase.
          </p>

          <Section title="Information We Collect">
            <p>We may collect the following types of information:</p>
            <ul className="list-disc pl-5 space-y-1 mt-2">
              <li><strong>Personal Information:</strong> Name, email address, phone number, and delivery address provided when you register or place an order.</li>
              <li><strong>Order Data:</strong> Products purchased, payment method, and order history.</li>
              <li><strong>Usage Data:</strong> Pages visited, search queries, browser type, and IP address.</li>
              <li><strong>Communication Data:</strong> Messages sent through our contact form.</li>
            </ul>
          </Section>

          <Section title="How We Use Your Information">
            <ul className="list-disc pl-5 space-y-1">
              <li>To process and fulfill your orders.</li>
              <li>To send order confirmations and delivery updates.</li>
              <li>To respond to your inquiries and support requests.</li>
              <li>To improve our website and product offerings.</li>
              <li>To send promotional communications (you may opt out at any time).</li>
            </ul>
          </Section>

          <Section title="Information Sharing">
            <p>We do not sell, trade, or rent your personal information to third parties. We may share data only in the following circumstances:</p>
            <ul className="list-disc pl-5 space-y-1 mt-2">
              <li>With trusted logistics partners to fulfill deliveries.</li>
              <li>When required by law or to protect our legal rights.</li>
              <li>With your explicit consent.</li>
            </ul>
          </Section>

          <Section title="Data Security">
            <p>We implement industry-standard security measures including SSL encryption, secure servers, and access controls to protect your personal information. However, no method of internet transmission is 100% secure.</p>
          </Section>

          <Section title="Cookies">
            <p>Our website may use cookies to enhance your browsing experience. Cookies are small files stored on your device. You can disable cookies in your browser settings, though this may affect some website functionality.</p>
          </Section>

          <Section title="Your Rights">
            <p>You have the right to:</p>
            <ul className="list-disc pl-5 space-y-1 mt-2">
              <li>Access the personal data we hold about you.</li>
              <li>Request correction of inaccurate data.</li>
              <li>Request deletion of your account and associated data.</li>
              <li>Opt out of marketing communications.</li>
            </ul>
            <p className="mt-2">To exercise these rights, contact us at <a href={`mailto:${settings.email}`} className="text-primary-600 hover:underline">{settings.email}</a>.</p>
          </Section>

          <Section title="Changes to This Policy">
            <p>We may update this Privacy Policy periodically. Changes will be posted on this page with an updated date. Continued use of our website after changes constitutes acceptance of the new policy.</p>
          </Section>

          <Section title="Contact Us">
            <p>If you have questions about this Privacy Policy, please contact us:</p>
            <div className="mt-3 bg-gray-50 rounded-xl p-4 space-y-1">
              <p><strong>{siteName}</strong></p>
              {settings.address && <p>{settings.address}</p>}
              {settings.email && <p>Email: <a href={`mailto:${settings.email}`} className="text-primary-600">{settings.email}</a></p>}
              {settings.phone && <p>Phone: {settings.phone}</p>}
            </div>
          </Section>

          <div className="border-t border-gray-100 pt-6 flex flex-wrap gap-4 text-sm">
            <Link to="/terms" className="text-primary-600 hover:underline font-medium">Terms of Service</Link>
            <Link to="/contact" className="text-primary-600 hover:underline font-medium">Contact Us</Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicyPage;
